import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import { toast } from 'react-toastify';
import classes from './mergeModalContent.module.scss';
import Button from '../../../_common/Form/Buttons/Button';
import SelectWithSearch from '../../../_common/Form/Selects/SelectWithSearch/SelectWithSearch';
import api from '../../../../api/api';
import InputField from '../../../_common/Form/TextFields/InputField';
import Select from '../../../_common/Form/Selects/Select/Select';
import { ReactComponent as ArrowIcon } from '../../../../assets/icons/arrow-right.svg';
import utils from '../../../../utils/utils';
import AttentionModal from '../../../_common/Modal/AttentionModal/AttentionModal';
import accountHelpers from '../../../../utils/accountHelpers';
import { stateOptions } from '../../../../utils/data/data';

let timeout;

const physicianInfoTemplate = [
  {
    path: 'firstName',
    label: 'First Name',
    type: 'input',
    validation: 'required',
  },
  {
    path: 'lastName',
    label: 'Last Name',
    type: 'input',
    validation: 'required',
  },
  {
    path: 'group',
    label: 'Group',
    type: 'input',
  },
  {
    path: 'rep',
    label: 'Rep',
    type: 'select',
    options: stateOptions,
  },
  {
    path: 'specialization',
    label: 'Specialization',
    type: 'select',
    options: stateOptions,
  },
  {
    path: 'DEA',
    label: 'DEA',
    type: 'input',
  },
  {
    path: 'NPI',
    label: 'NPI',
    type: 'input',
  },
  {
    path: 'phone',
    label: 'Phone',
    type: 'input',
    validation: 'phone',
  },
  {
    path: 'fax',
    label: 'Fax',
    type: 'input',
  },
  {
    path: 'email',
    label: 'Email',
    type: 'input',
    validation: 'email',
  },
  {
    path: 'contact',
    label: 'Contact',
    type: 'input',
  },
  {
    path: 'physicianWarning',
    label: 'Physician Warning',
    type: 'input',
  },
  {
    path: 'addressStreet',
    label: 'Address - Street',
    type: 'input',
  },
  {
    path: 'addressCity',
    label: 'Address - City',
    type: 'input',
  },
  {
    path: 'addressState',
    label: 'Address - State',
    type: 'select',
    options: stateOptions,
  },
  {
    path: 'addressZipCode',
    label: 'Address - ZipCode',
    type: 'input',
    validation: 'number',
  },
];

const MergeModalContent = ({ onCancel }) => {
  const [deletingPhysicianList, setDeletingPhysicianList] = useState({
    deletingPhysicians: [],
    updatingPhysicians: [],
  });

  const [searchPhysicianValue, setSearchPhysicianValue] = useState({
    deletingValue: '',
    updatingValue: '',
  });

  const [selectedPhysician, setSelectedPhysician] = useState({
    deletingPhysician: {},
    updatingPhysician: {},
  });

  const [updatingPhysicianDefaultData, setUpdatingPhysicianDefaultData] = useState({});

  const { deletingPhysicians, updatingPhysicians } = deletingPhysicianList;

  const { deletingValue, updatingValue } = searchPhysicianValue;

  const { deletingPhysician, updatingPhysician } = selectedPhysician;

  const [searchPhysicianLoading, setSearchPhysicianLoading] = useState('');

  const [errors, setErrors] = useState({});

  const [isAttentionModalOpen, attentionModalToggle] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const searchPhysician = (path, value) => {
    setSearchPhysicianValue((prev) => ({ ...prev, [`${path}Value`]: value }));

    clearTimeout(timeout);
    setDeletingPhysicianList((prev) => ({ ...prev, [`${path}Physicians`]: [] }));

    if (value.trim()) {
      setSearchPhysicianLoading(path);

      timeout = setTimeout(async () => {
        const { data: { data: { response } } } = await api.getPhysicians({ limit: 100, name: value });
        const physicians = response.map((r) => ({ ...r, name: `${r.firstName} ${r.lastName}` }))
          .filter(({ id }) => id !== (path === 'deleting' ? updatingPhysician?.id : deletingPhysician?.id));

        setDeletingPhysicianList((prev) => ({ ...prev, [`${path}Physicians`]: physicians }));

        setSearchPhysicianLoading('');
      }, 400);
    } else {
      setSearchPhysicianLoading('');
    }
  };

  const setPhysician = (path, option, searchValue) => {
    const physician = { ...option };

    delete physician.updatedAt;
    delete physician.createdAt;

    setSearchPhysicianValue((prev) => ({ ...prev, [`${path}Value`]: searchValue }));

    setSelectedPhysician((prev) => ({ ...prev, [`${path}Physician`]: physician }));

    if (path === 'updating') {
      setUpdatingPhysicianDefaultData(physician);
      setErrors({});
    }
  };

  const changePhysician = (path, value) => {
    if (path === 'all') {
      setErrors({});

      const updatingFields = {};

      physicianInfoTemplate.forEach((d) => {
        updatingFields[d.path] = deletingPhysician[d.path];
      });

      setSelectedPhysician((prev) => ({
        ...prev,
        updatingPhysician: {
          ...prev.updatingPhysician,
          ...updatingFields,
          repId: deletingPhysician.repId,
        },
      }));
    } else {
      setErrors((prev) => ({ ...prev, [path]: '' }));

      if (path === 'rep') {
        setSelectedPhysician((prev) => ({
          ...prev,
          updatingPhysician: {
            ...prev.updatingPhysician,
            [path]: value,
            repId: deletingPhysician.repId,
          },
        }));
      } else {
        setSelectedPhysician((prev) => ({
          ...prev,
          updatingPhysician: {
            ...prev.updatingPhysician,
            [path]: value,
          },
        }));
      }
    }
  };

  const onKeyPress = (e, validationType, isPaste) => {
    if (validationType === 'number') {
      utils.keyPressOnlyNumber(e, isPaste);
    } else if (validationType === 'phone') {
      utils.keyPressPhoneNumber(e, isPaste);
    } else {
      () => {};
    }
  };

  const margePatient = () => {
    let hasError = false;

    physicianInfoTemplate.forEach(({ path, validation }) => {
      if (validation === 'required' && !updatingPhysician[path]) {
        setErrors((prev) => ({ ...prev, [path]: 'Field is required' }));
        hasError = true;
      }
      // else if (validation === 'email' && updatingPhysician[path] && !utils.isEmail(updatingPhysician[path])) {
      //   setErrors((prev) => ({ ...prev, [path]: 'Invalid email' }));
      //   hasError = true;
      // }
    });

    if (!hasError) {
      attentionModalToggle(true);
    } else {
      toast.error('Please correct all errors');
    }
  };

  const onSave = async () => {
    setSaveLoading(true);

    await Promise.all([
      api.mergePhysicianScripts({
        oldPhysicianId: deletingPhysician.id,
        physicianId: updatingPhysician.id,
        userId: accountHelpers.getUser().id,
      }),
      api.mageNotePhysician({
        oldPhysicianId: deletingPhysician.id,
        physicianId: updatingPhysician.id,
      }),
    ]);

    await api.deletePhysician({ physicianId: deletingPhysician.id, userId: accountHelpers.getUser().id });

    const data = {
      ...updatingPhysician,
    };

    delete data.name;
    delete data.Representative;

    try {
      await api.updatePhysician(data);

      window.location.reload();
    } catch (e) {
      console.warn(e);
    }

    setSaveLoading(false);
  };

  return (
    <div className={classes.wrapper}>
      <h2>Merge Physician</h2>

      <div className={classes.btns_wrapper}>
        <Button
          title="Cancel"
          type="button"
          clearButton
          onClick={onCancel}
        />

        <Button
          title="Merge"
          type="button"
          reverseColor
          onClick={margePatient}
          disabled={!deletingPhysician.id || !updatingPhysician.id}
        />
      </div>

      <div className={classes.content}>
        <div className={classes.fields_wrapper}>
          <div className={classes.field}>
            <SelectWithSearch
              label="Type name or physician"
              searchValue={deletingValue}
              options={deletingPhysicians}
              optionKey=""
              loading={searchPhysicianLoading === 'deleting'}
              onChange={(value) => searchPhysician('deleting', value)}
              onSelectOption={(option, searchValue) => setPhysician('deleting', option || {}, searchValue)}
              zIndex={30}
            />

            {deletingPhysician.id && <div className={classes.attention_text}>This physician will be deleted!</div>}
          </div>

          <div className={classes.field}>
            <SelectWithSearch
              label="Type name or physician"
              searchValue={updatingValue}
              options={updatingPhysicians}
              optionKey=""
              loading={searchPhysicianLoading === 'updating'}
              onChange={(value) => searchPhysician('updating', value)}
              onSelectOption={(option, searchValue) => setPhysician('updating', option || {}, searchValue)}
              zIndex={30}
            />

            {updatingPhysician.id && <div className={classes.attention_text}>This physician will be updated!</div>}
          </div>
        </div>

        <h3>Physician Info</h3>

        <div className={classes.select_all}>
          <Button
            className={classes.marge_btn}
            title="Merge All"
            buttonColor="#0087b0"
            reverseColor
            onClick={() => changePhysician('all')}
            disabled={!deletingPhysician.id || !updatingPhysician.id}
          />
        </div>

        <div className={classes.user_info}>
          {physicianInfoTemplate.map(({
            path, label, type, validation, options,
          }) => (
            <div key={path} className={classes.info_field_rapper}>
              <InputField
                onChange={() => {}}
                placeholder={(type === 'calendar' && deletingPhysician[path]
                  ? moment(deletingPhysician[path]).format('MM/DD/YYYY') : deletingPhysician[path]) || '-'}
                disabled
                label={label}
              />

              <Button
                className={classes.marge_btn}
                title={(
                  <ArrowIcon />
                )}
                buttonColor="#0087b0"
                reverseColor
                onClick={() => {
                  if (!_.isEmpty(deletingPhysician)) {
                    changePhysician(path, deletingPhysician[path]);
                  }
                }}
              />

              { type === 'select'
                ? (
                  <Select
                    label={label}
                    onChange={(option) => changePhysician(path, option)}
                    currentOption={updatingPhysician[path] || ''}
                    options={options}
                    placeholder={updatingPhysicianDefaultData[path] || '-'}
                    zIndex={30}
                    closeOnScroll
                  />
                )
                : (
                  <InputField
                    label={label}
                    value={updatingPhysician[path] || ''}
                    placeholder={updatingPhysicianDefaultData[path] || '-'}
                    onChange={(value) => changePhysician(path, value)}
                    error={errors[path]}
                    errorClassName={classes.error_text}
                    onKeyPress={(e) => onKeyPress(e, validation)}
                    onPaste={(e) => onKeyPress(e, validation, true)}
                  />
                )}
            </div>
          ))}
        </div>
      </div>

      <AttentionModal
        modalClassName={classes.attention_modal}
        isOpen={isAttentionModalOpen}
        onClose={() => attentionModalToggle(false)}
        title={`This will delete ${deletingPhysician.name} and update ${updatingPhysician.name}`}
        text={`The scripts associated with ${deletingPhysician.name} will be reassigned to ${updatingPhysician.name}`}
        zIndex={31}
        onSave={onSave}
        loading={saveLoading}
      />
    </div>
  );
};

MergeModalContent.propTypes = {
  onCancel: PropTypes.func.isRequired,
};

export default MergeModalContent;
